import Vue from 'vue';
import VueX, { StoreOptions, ActionContext } from 'vuex';
import axios from 'axios';
import createPersistedState from 'vuex-persistedstate';

import { DistBarData } from '@/interfaces/types/ChartTypes';
import { axiosApiInstance } from '../utils/axiosInstances';
import { SimilarityAnalysisResult } from '@/interfaces/types/AnalysisResult';

Vue.use(VueX);

interface analysisResultI {
  badDataNumber: number;
  totalDataNumber: number;
  badFileSizeList: {
    file: string;
    file_size: number;
  }[];
  badResolutionList: {
    file: string;
    height: number;
    width: number;
  }[];
  corruptedFileList: {
    file: string;
    target_lib: string;
  }[];
  badDurationList: {
    file: string;
    duration: number;
  }[];
  fileAnalyses: {
    extension_distribution: { [key: string]: number };
    file_size_distribution: { bars: { [key: string]: number } };
    resolution_distribution: {
      wbins: number;
      wmin: number;
      hbins: number;
      hmin: number;
      values: Array<Array<number>>;
    };
    most_frequent_words: Array<[string, number]>;
    text_len_distribution: DistBarData;
    word_len_distribution: DistBarData;
    word_per_text_distribution: DistBarData;
    text_per_file: Array<[string, number]>;
    similarity_analysis_result?: SimilarityAnalysisResult;
    sample_rate_distribution?: Record<string, number>;
    duration_distribution?: DistBarData;
  };
}

interface schemaAnalysisResultI {
  totalLabelNumber: number;
  badLabelNumber: number;
  violationPerProp: {
    [key: string]: {
      count: number;
      violationList: Array<unknown>;
      percent: number;
    };
  };
}

interface labelAnalysisResultI {
  [key: string]: any;
}

interface storeStateI {
  analysisResult: analysisResultI;
  schemaAnalysisResult: schemaAnalysisResultI;
  labelAnalysisResult: labelAnalysisResultI;
  isLoading: boolean;
  isAgentAlive: boolean;
  accessToken: string;
  userId: string;
  userName: string;
  isAdmin?: boolean;
  refreshToken: string;
  GNBDirectory: string[];
}

const store: StoreOptions<storeStateI> = {
  state: {
    analysisResult: {
      badDataNumber: 0,
      totalDataNumber: 0,
      badFileSizeList: [],
      badResolutionList: [],
      corruptedFileList: [],
      badDurationList: [],
      fileAnalyses: {
        extension_distribution: {},
        file_size_distribution: {
          bars: {},
        },
        resolution_distribution: {
          wbins: 0,
          wmin: 0,
          hbins: 0,
          hmin: 0,
          values: [[]],
        },
        most_frequent_words: [],
        text_len_distribution: { bars: {} },
        word_len_distribution: { bars: {} },
        word_per_text_distribution: { bars: {} },
        text_per_file: [],
        similarity_analysis_result: undefined,
        duration_distribution: undefined,
        sample_rate_distribution: undefined,
      },
    },
    schemaAnalysisResult: {
      badLabelNumber: 0,
      totalLabelNumber: 0,
      violationPerProp: {},
    },
    labelAnalysisResult: {},
    isLoading: false,
    isAgentAlive: true,
    accessToken: '',
    refreshToken: '',
    userId: '',
    userName: '',
    isAdmin: undefined,
    GNBDirectory: [],
  },
  mutations: {
    setAnalysisResult(state: storeStateI, payload: analysisResultI) {
      state.analysisResult = payload;
    },
    setLabelAnalysisResult(state: storeStateI, payload: labelAnalysisResultI) {
      state.labelAnalysisResult = payload;
    },
    setSchemaAnalysisResult(
      state: storeStateI,
      payload: schemaAnalysisResultI,
    ) {
      state.schemaAnalysisResult = payload;
    },
    startLoading(state: storeStateI) {
      state.isLoading = true;
    },
    stopLoading(state: storeStateI) {
      state.isLoading = false;
    },
    setAgentAliveStatus(state: storeStateI, payload: boolean) {
      state.isAgentAlive = payload;
    },
    setToken(
      state: storeStateI,
      payload: {
        accessToken?: string;
        refreshToken?: string;
        userId?: string;
      },
    ) {
      if (payload.refreshToken !== undefined) {
        state.refreshToken = payload.refreshToken;
      }

      if (payload.accessToken !== undefined) {
        state.accessToken = payload.accessToken;
      }

      if (payload.userId !== undefined) {
        state.userId = payload.userId;
      }
    },
    setUserName(state: storeStateI, payload: string) {
      if (payload !== '') {
        state.userName = payload;
      }
    },
    setIsAdmin(state: storeStateI, payload?: boolean) {
      state.isAdmin = payload;
    },
    setSignOut(state: storeStateI) {
      state.userId = '';
      state.userName = '';
      state.accessToken = '';
      state.refreshToken = '';
      state.isAdmin = undefined;
    },
    clearAnalysis(state: storeStateI) {
      state.analysisResult = {
        badDataNumber: 0,
        totalDataNumber: 0,
        badFileSizeList: [],
        badResolutionList: [],
        corruptedFileList: [],
        badDurationList: [],
        fileAnalyses: {
          extension_distribution: {},
          file_size_distribution: {
            bars: {},
          },
          resolution_distribution: {
            wbins: 0,
            wmin: 0,
            hbins: 0,
            hmin: 0,
            values: [[]],
          },
          most_frequent_words: [],
          text_len_distribution: { bars: {} },
          word_len_distribution: { bars: {} },
          word_per_text_distribution: { bars: {} },
          text_per_file: [],
          similarity_analysis_result: undefined,
          duration_distribution: undefined,
          sample_rate_distribution: undefined,
        },
      };
      state.schemaAnalysisResult = {
        badLabelNumber: 0,
        totalLabelNumber: 0,
        violationPerProp: {},
      };
      state.labelAnalysisResult = {};
    },
  },
  getters: {
    // #region fileAnalyses
    fileAnalyses(state: storeStateI) {
      return state.analysisResult.fileAnalyses;
    },
    badDataNumber(state: storeStateI) {
      return state.analysisResult.badDataNumber;
    },
    totalDataNumber(state: storeStateI) {
      return state.analysisResult.totalDataNumber;
    },
    badFileSizeList(state: storeStateI) {
      return state.analysisResult.badFileSizeList;
    },
    badResolutionList(state: storeStateI) {
      return state.analysisResult.badResolutionList;
    },
    corruptedFileList(state: storeStateI) {
      return state.analysisResult.corruptedFileList;
    },
    badDurationList(state: storeStateI) {
      return state.analysisResult.badDurationList;
    },
    extensionDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses.extension_distribution;
    },
    fileSizeDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses.file_size_distribution;
    },
    resolutionDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses.resolution_distribution;
    },
    wordCloudData(state: storeStateI) {
      return state.analysisResult.fileAnalyses.most_frequent_words;
    },
    textLenDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses.text_len_distribution;
    },
    wordLenDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses.word_len_distribution;
    },
    wordPerTextDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses.word_per_text_distribution;
    },
    similarityAnalysis(state: storeStateI) {
      return state.analysisResult.fileAnalyses
        ? state.analysisResult.fileAnalyses.similarity_analysis_result
        : undefined;
    },
    durationDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses
        ? state.analysisResult.fileAnalyses.duration_distribution
        : undefined;
    },
    sampleRateDistribution(state: storeStateI) {
      return state.analysisResult.fileAnalyses
        ? state.analysisResult.fileAnalyses.sample_rate_distribution
        : undefined;
    },
    // #endregion

    isLoading(state: storeStateI) {
      return state.isLoading;
    },
    isAgentAlive(state: storeStateI) {
      return state.isAgentAlive;
    },
    refreshToken(state: storeStateI) {
      return state.refreshToken;
    },
    accessToken(state: storeStateI) {
      return state.accessToken;
    },
    userId(state: storeStateI) {
      return state.userId;
    },
    userName(state: storeStateI) {
      return state.userName;
    },
    isAdmin(state: storeStateI) {
      return state.isAdmin;
    },
    GNBDirectory(state: storeStateI) {
      return state.GNBDirectory;
    },
    /** Labelset analysis results */
    schemaAnalysisResult(state: storeStateI) {
      return state.schemaAnalysisResult;
    },
    labelAnalysisResult(state: storeStateI) {
      return state.labelAnalysisResult;
    },
  },
  actions: {
    /**
     * 데이터 분석 결과를 전역 상태에 저장한다. 주로 대시보드 화면 접근 시 호출. 이하 region 소속 함수는 설명 동일
     * @param param0
     * @param payload
     */
    // #region get dataset/labelset analysis data
    async getChartAnalysisData(
      { commit }: ActionContext<storeStateI, storeStateI>,
      payload: { APIURL: string },
    ) {
      commit('startLoading');
      try {
        const result = await axiosApiInstance.get(payload.APIURL);
        let parsedResult = result.data.result;
        parsedResult = {
          badDataNumber: parsedResult.bad_data_number,
          totalDataNumber: parsedResult.total_data_number,
          badFileSizeList: parsedResult.bad_file_size_list,
          badResolutionList: parsedResult.bad_resolution_list,
          badDurationList: parsedResult.bad_duration_list,
          corruptedFileList: parsedResult.corrupted_file_list,
          fileAnalyses: parsedResult.file_analysises,
        };
        commit('setAnalysisResult', parsedResult);
        commit('stopLoading');
      } catch (error) {
        commit('stopLoading');
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          alert('소유하지 않은 데이터셋입니다. 다른 계정으로 접속해주세요.');
          window.location.href = '/home';
        } else {
          // Unidentified error
          console.error(error);
          alert('데이터셋 분석 정보를 가져오는 중 에러가 발생했습니다.');
        }
      }
    },
    async getSchemaChartAnalysisData(
      { commit }: ActionContext<storeStateI, storeStateI>,
      payload: { APIURL: string },
    ) {
      commit('startLoading');
      const result = await axiosApiInstance.get(
        payload.APIURL + '/schema?type=prop',
      );
      let parsedResult = result.data.result;
      parsedResult = {
        totalLabelNumber: parsedResult.total_label_number,
        badLabelNumber: parsedResult.bad_label_number,
        violationPerProp: parsedResult.violation_per_item,
      };
      commit('setSchemaAnalysisResult', parsedResult);
      commit('stopLoading');
    },
    async getLabelChartAnalysisData(
      { commit }: ActionContext<storeStateI, storeStateI>,
      payload: { APIURL: string },
    ) {
      commit('startLoading');
      const result = await axiosApiInstance.get(
        payload.APIURL + '/analysis/label',
      );
      const parsedResult = result.data.result.analysis;
      commit('setLabelAnalysisResult', parsedResult);
      commit('stopLoading');
    },
    // #endregion
  },
};

export default new VueX.Store({ ...store, plugins: [createPersistedState()] });
